(() => {
  if (customElements.get('fp-deferred-featured-collection') && customElements.get('fp-deferred-pp-collection')) return;

  const pending = [];
  let busy = false;
  let stopped = false;
  let suspendedUntil = 0;
  let nextAllowedAt = 0;
  let wakeTimer = null;

  const runNext = async () => {
    if (busy || stopped) return;
    if (Date.now() < nextAllowedAt) {
      if (!wakeTimer) wakeTimer = setTimeout(() => {
        wakeTimer = null;
        runNext();
      }, nextAllowedAt - Date.now());
      return;
    }
    const section = pending.shift();
    if (!section) return;
    if (!section.isConnected) return runNext();

    busy = true;
    try {
      await section.load();
    } catch (error) {
      section.showError();
      // A server or rate-limit failure should not trigger a cascade of requests.
      stopped = true;
      for (const queued of pending.splice(0)) queued.showError();
    } finally {
      busy = false;
      // A rapid scroll should not turn a serial queue into a burst of Liquid renders.
      nextAllowedAt = Date.now() + 300;
      runNext();
    }
  };

  class FPDeferredShelf extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.lifecycle = new AbortController();
      this.retryButton = this.querySelector('[data-fp-deferred-retry]');
      this.status = this.querySelector('[data-fp-deferred-status]');
      this.retryButton?.addEventListener('click', () => {
        if (Date.now() < suspendedUntil) return this.showError();
        stopped = false;
        this.enqueue();
      }, { signal: this.lifecycle.signal });

      if (!('IntersectionObserver' in window)) {
        this.enqueue();
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.enqueue();
        }
      }, { rootMargin: '250px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.lifecycle?.abort();
      this.controller?.abort();
      const index = pending.indexOf(this);
      if (index !== -1) pending.splice(index, 1);
      this.queued = false;
      this.initialized = false;
    }

    enqueue() {
      if (this.queued || !this.isConnected) return;
      if (stopped) return this.showError();
      this.queued = true;
      if (this.retryButton) {
        this.retryButton.hidden = true;
        this.retryButton.style.display = 'none';
      }
      if (this.status) this.status.textContent = 'Carregando produtos…';
      pending.push(this);
      runNext();
    }

    showError() {
      this.queued = false;
      if (!this.isConnected) return;
      if (this.status) this.status.textContent = Date.now() < suspendedUntil
        ? 'Aguarde um momento antes de tentar novamente.'
        : 'Não foi possível carregar esta vitrine.';
      if (this.retryButton) {
        this.retryButton.hidden = false;
        this.retryButton.style.display = '';
      }
      const fallback = this.querySelector('[data-fp-deferred-fallback]');
      if (fallback) fallback.hidden = false;
    }

    async load() {
      const id = this.dataset.sectionId;
      if (!id) throw new Error('Section ID ausente');
      const url = new URL(location.href);
      url.searchParams.delete('sections');
      // Shopify may remove this parameter from the visible URL after opening a draft.
      const theme = window.Shopify?.theme;
      if (theme?.id && theme.role === 'unpublished') {
        url.searchParams.set('preview_theme_id', String(theme.id));
      }
      url.searchParams.set('section_id', id);
      const lifecycle = this.lifecycle;
      const controller = this.controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (response.status === 429) {
          const retry = response.headers.get('Retry-After')?.trim();
          const delay = retry && /^\d+$/.test(retry) ? Number(retry) * 1000 : Date.parse(retry) - Date.now();
          suspendedUntil = Date.now() + (Number.isFinite(delay) && delay >= 0 ? delay : 60000);
        }
        if (!response.ok) throw new Error(`Section Rendering HTTP ${response.status}`);
        const html = await response.text();
        if (!this.isConnected || lifecycle.signal.aborted) return;
        const rendered = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${id}`);
        const current = document.getElementById(`shopify-section-${id}`);
        if (!rendered || !current || html.includes('Liquid error') || rendered.querySelector('fp-deferred-featured-collection, fp-deferred-pp-collection')) {
          throw new Error('Section Rendering incompleto');
        }
        const content = Array.from(rendered.children).find((child) => child.tagName === 'DIV');
        if (!content) throw new Error('Conteúdo da vitrine ausente');
        const placeholder = Array.from(current.children).find((child) =>
          child.matches?.('fp-deferred-featured-collection, .container-pp-collection-slideshow')
        );
        if (!placeholder) throw new Error('Placeholder da vitrine ausente');
        const skeleton = document.createElement('div');
        skeleton.setAttribute('aria-hidden', 'true');
        skeleton.style.cssText = 'position:absolute;inset:0;z-index:1;pointer-events:none';
        skeleton.append(placeholder.cloneNode(true));
        skeleton.querySelectorAll('fp-deferred-featured-collection, fp-deferred-pp-collection').forEach((element) => {
          const inert = document.createElement('div');
          inert.className = element.className;
          inert.innerHTML = element.innerHTML;
          element.replaceWith(inert);
        });
        rendered.style.height = `${current.getBoundingClientRect().height}px`;
        rendered.style.overflow = 'hidden';
        rendered.style.position = 'relative';
        content.style.visibility = 'hidden';
        rendered.append(skeleton);
        current.replaceWith(rendered);
        rendered.dispatchEvent(new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: id },
        }));
        setTimeout(() => {
          if (!rendered.isConnected) return;
          rendered.style.height = '';
          rendered.style.overflow = '';
          content.style.visibility = '';
          skeleton.remove();
        }, 1800);
      } catch (error) {
        // Removing/reordering a section is not a storefront failure.
        if (!lifecycle.signal.aborted) throw error;
      } finally {
        clearTimeout(timeout);
        if (this.controller === controller) this.controller = null;
      }
    }
  }

  if (!customElements.get('fp-deferred-featured-collection')) {
    customElements.define('fp-deferred-featured-collection', class extends FPDeferredShelf {});
  }
  if (!customElements.get('fp-deferred-pp-collection')) {
    customElements.define('fp-deferred-pp-collection', class extends FPDeferredShelf {});
  }
})();
